import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import {
  DEADLINE_COLORS,
  daysUntil,
  deadlineTone,
  isOpenProject,
  parseDeadline,
  projectPath,
  relativeDeadline,
} from "./dashboardFormat";

const MAX_ROWS = 5;
const PASSED_WINDOW_DAYS = 30;

const DeadlinesCard = ({ projects, loading }) => {
  const { rows, counts } = useMemo(() => {
    const today = new Date();
    const dated = projects
      .filter(isOpenProject)
      .map((p) => {
        const date = parseDeadline(p.deadline);
        return date ? { project: p, date, days: daysUntil(date, today) } : null;
      })
      .filter((d) => d && d.days >= -PASSED_WINDOW_DAYS)
      .sort((a, b) => a.date - b.date);
    const tally = { passed: 0, soon: 0, later: 0 };
    dated.forEach((d) => {
      tally[deadlineTone(d.days)] += 1;
    });
    return { rows: dated.slice(0, MAX_ROWS), counts: tally };
  }, [projects]);

  return (
    <section className="bg-white border border-black/[0.08] rounded-xl shadow-[0_1px_3px_rgba(24,16,28,0.05)] px-5 py-[18px] flex flex-col min-w-0">
      <h2 className="m-0 text-[16px] font-medium text-[#18101C]">Project deadlines</h2>

      <div className="flex flex-wrap gap-3 mt-2 mb-3 text-[12px] text-[#73607B]">
        {[
          ["passed", "passed"],
          ["soon", "due soon"],
          ["later", "due later"],
        ].map(([tone, label]) => (
          <span key={tone} className="flex items-center gap-1.5">
            <span className="w-[7px] h-[7px] rounded-full" style={{ background: DEADLINE_COLORS[tone] }} />
            {counts[tone]} {label}
          </span>
        ))}
      </div>

      {loading && !projects.length ? (
        <div className="flex flex-col gap-3 animate-pulse">
          {Array.from({ length: 4 }, (_, i) => (
            <div key={i} className="h-11 rounded-lg bg-[#F7F4F8]" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <p className="m-0 py-6 text-center text-[13px] text-[#73607B] border-t border-[#F1EDF3]">
          No upcoming deadlines. They appear here once a project has an RFP with a due date.
        </p>
      ) : (
        <ul className="m-0 p-0 list-none flex flex-col">
          {rows.map(({ project, date, days }) => (
            <li key={project.id} className="border-t border-[#F1EDF3]">
              <Link
                to={projectPath(project)}
                state={{ folderId: project.id, folderName: project.name }}
                className="flex items-center gap-3 py-2.5 rounded-md hover:bg-[#F8F3FF] -mx-2 px-2"
              >
                <span className="w-11 flex-shrink-0 text-center rounded-lg bg-[#F8F3FF] py-1">
                  <span className="block text-[16px] font-semibold text-[#18101C] leading-tight">
                    {String(date.getDate()).padStart(2, "0")}
                  </span>
                  <span className="block text-[10px] font-medium uppercase tracking-[0.04em] text-[#73607B]">
                    {date.toLocaleString("en-US", { month: "short" })}
                  </span>
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block text-[14px] text-[#171717] truncate">{project.name}</span>
                  <span className="flex items-center gap-1.5 text-[12px] text-[#73607B]">
                    <span className="w-[7px] h-[7px] rounded-full" style={{ background: DEADLINE_COLORS[deadlineTone(days)] }} />
                    {relativeDeadline(days)}
                    {Number.isFinite(project.document_count) && (
                      <>
                        <span className="text-[#C4B8C9]">·</span>
                        {project.document_count} doc{project.document_count === 1 ? "" : "s"}
                      </>
                    )}
                  </span>
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

export default DeadlinesCard;
